import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import BalanceRow from "../../components/leaves/BalanceRow";
import LeaveCard from "../../components/leaves/LeaveCard";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { Plus, Sparkles, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import StickyHeading from "../../components/common/StickyHeading";

export default function MyLeavesPage({ soId = null }) {
  const { user } = useAuth();
  const [balance, setBalance] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function fetchAll() {
    setLoading(true);
    try {
      const [{ data: bal }, { data: rows }] = await Promise.all([
        api.get(`/leaves/balance/${user.id}`),
        api.get("/leaves", { params: soId ? { so_id: soId } : {} }),
      ]);
      setBalance(bal);
      setLeaves(rows);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (user?.id) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  async function cancelLeave(id) {
    try {
      await api.put(`/leaves/${id}/cancel`);
      toast.success("Leave cancelled");
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="space-y-6" data-testid="my-leaves-page">
      <StickyHeading
        icon={Sparkles}
        label="My Leave"
        title="My leaves"
        subtitle="Apply for casual or privilege leave and track your balance — sickness files under CL."
        testId="my-leaves-heading"
        actions={
          <Link
            to="/leaves/apply"
            className="inline-flex items-center justify-center gap-1.5 rounded-md bg-callisto-navy text-white h-11 px-5 text-sm font-semibold hover:bg-callisto-navy/90"
            data-testid="apply-leave-cta"
          >
            <Plus className="h-4 w-4" />
            Apply for leave
          </Link>
        }
      />

      {/* Balance */}
      {loading || !balance ? (
        <div className="grid grid-cols-2 gap-3">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
      ) : (
        <BalanceRow balance={balance} testIdPrefix="my-leaves-balance" />
      )}

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
          {error}
        </div>
      )}

      {/* History */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      ) : leaves.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="my-leaves-list">
          {leaves.map((l) => (
            <LeaveCard
              key={l.id}
              leave={l}
              action={
                l.status === "pending" || l.status === "approved" ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => cancelLeave(l.id)}
                    className="h-7 px-2.5 text-[11px] border-red-200 text-red-700 hover:bg-red-50"
                    data-testid={`my-leaves-cancel-${l.id}`}
                  >
                    Cancel
                  </Button>
                ) : null
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white border border-callisto rounded-xl p-10 flex flex-col items-center text-center">
      <div className="h-14 w-14 rounded-xl bg-callisto-navy/5 flex items-center justify-center mb-3">
        <CalendarDays className="h-6 w-6 text-callisto-navy" />
      </div>
      <p className="text-sm font-semibold text-callisto-navy">No leaves yet</p>
      <p className="mt-1 text-xs text-callisto-muted max-w-sm">
        Once you apply for a leave it will show up here with its current status.
      </p>
    </div>
  );
}
